import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';
import { classnames } from 'helpers/utils/classnames';

export interface CustomDropdownProps {
  buttonElement?: JSX.Element;
  buttonClassNames?: (open?: boolean) => string | string;
  extraButtonClassName?: string;
  menuWrapperClassNames?: string;
  menuClassNames?: (open?: boolean) => string | string;
  disabled?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Keep the closed menu in the DOM (display: none) instead of unmounting it. */
  keepMenuMounted?: boolean;
}

const CustomDropDown = ({
  buttonElement,
  buttonClassNames,
  extraButtonClassName = '',
  menuWrapperClassNames,
  menuClassNames,
  disabled = false,
  isOpen: isOpenProp,
  onOpenChange,
  keepMenuMounted = false,
  children,
}: React.PropsWithChildren<CustomDropdownProps>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastToggleTime = useRef<number>(0);

  // Support both controlled and uncontrolled state
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = isOpenProp !== undefined ? isOpenProp : internalIsOpen;

  const handleToggle = useCallback(
    (nextOpen: boolean) => {
      const now = Date.now();
      if (now - lastToggleTime.current < 200) return;
      lastToggleTime.current = now;

      if (isOpenProp === undefined) {
        setInternalIsOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [onOpenChange, isOpenProp],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleToggle(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onOpenChange]);

  const defaultButtonClassNames = useCallback(
    (open?: boolean) => {
      return classnames(
        'flex h-40 w-full items-center justify-between border px-12 py-12',
        'focus:border-gray-500 focus:shadow-md active:border-stroke-primary disabled:border-stroke-primary',
        {
          'cursor-not-allowed bg-stroke-primary': disabled,
          'rounded-t-sm border-stroke-primary bg-white': open,
          'border-stroke-primary bg-white': !disabled && !open,
        },
      );
    },
    [disabled],
  );

  const defaultMenuClassNames = useCallback((open?: boolean) => {
    return `max-h-300 overflow-y-scroll rounded-b-sm border border-stroke-primary z-100`;
  }, []);

  return (
    <div className="w-full" ref={containerRef}>
      <div className={classnames('relative w-full', { 'z-50': isOpen })}>
        {buttonElement ? (
          React.cloneElement(buttonElement, {
            role: buttonElement.props.role ?? 'button',
            tabIndex: buttonElement.props.tabIndex ?? (disabled ? -1 : 0),
            'aria-haspopup': buttonElement.props['aria-haspopup'] ?? 'listbox',
            'aria-expanded': isOpen,
            onPointerDown: (e: React.PointerEvent) => {
              if (!disabled) handleToggle(!isOpen);
            },
            onClick: (e: React.MouseEvent) => {
              if (!disabled) handleToggle(!isOpen);
            },
            // Add className and other props if they don't exist
            className: classnames(
              buttonElement.props.className,
              buttonClassNames ? buttonClassNames(isOpen) : defaultButtonClassNames(isOpen),
              extraButtonClassName,
            ),
          })
        ) : (
          <div
            role="button"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            tabIndex={disabled ? -1 : 0}
            onPointerDown={() => !disabled && onOpenChange?.(!isOpen)}
            className={`${buttonClassNames ? buttonClassNames(isOpen) : defaultButtonClassNames(isOpen)} ${extraButtonClassName}`}
          >
            {/* Default content if no buttonElement */}
          </div>
        )}

        <Transition
          show={isOpen}
          unmount={!keepMenuMounted}
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform origin-top scale-y-0 opacity-0"
          enterTo="transform origin-top scale-y-100 opacity-100"
          leave="transition ease-in duration-100"
          leaveFrom="transform origin-top scale-y-100 opacity-100"
          leaveTo="transform origin-top scale-y-0 opacity-0"
        >
          <div className={menuWrapperClassNames ?? 'absolute left-0 top-[40px] z-10 w-full bg-white'}>
            <div className={menuClassNames ? menuClassNames(isOpen) : defaultMenuClassNames(isOpen)}>{children}</div>
          </div>
        </Transition>
      </div>
    </div>
  );
};

export default CustomDropDown;
